import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, Info, User, CreditCard, Car, Tags, Home, Phone, StickyNote, Save } from "lucide-react";

const fieldInputCls = (active, extra = "") =>
  `w-full bg-slate-900 border ${
    active ? "border-brand-500 ring-2 ring-brand-500/30" : "border-slate-700"
  } focus:border-brand-500 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none transition-colors ${extra}`;

function Field({ id, label, icon: Icon, value, onChange, placeholder, activeField, setActiveField, textarea, uppercase, type }) {
  const active = activeField === id;
  return (
    <div>
      <label className="block text-xs font-medium text-slate-300 mb-1">{label}</label>
      <div className="relative">
        <span
          className={`absolute ${textarea ? "top-3" : "inset-y-0"} left-0 pl-3 flex ${
            textarea ? "items-start" : "items-center"
          } pointer-events-none text-slate-400`}
        >
          <Icon size={12} />
        </span>
        {textarea ? (
          <textarea
            id={id}
            rows={2}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onClick={() => setActiveField(id)}
            onFocus={() => setActiveField(id)}
            placeholder={placeholder}
            className={fieldInputCls(active)}
          />
        ) : (
          <input
            id={id}
            type={type || "text"}
            required
            value={value}
            onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
            onClick={() => setActiveField(id)}
            onFocus={() => setActiveField(id)}
            placeholder={placeholder}
            className={fieldInputCls(active, uppercase ? "uppercase" : "")}
          />
        )}
      </div>
    </div>
  );
}

export default function FormView({ activeField, setActiveField, onSave, onBack, registerSetters, apiRef }) {
  const [fullName, setFullName] = useState("");
  const [plateNo, setPlateNo] = useState("");
  const [carModel, setCarModel] = useState("");
  const [relationDegree, setRelationDegree] = useState("");
  const [aptNo, setAptNo] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [infoNote, setInfoNote] = useState("");
  const formRef = useRef(null);

  useEffect(() => {
    registerSetters({
      fullName: (v) => setFullName(v),
      plateNo: (v) => setPlateNo(v.toUpperCase()),
      carModel: (v) => setCarModel(v),
      relationDegree: (v) => setRelationDegree(v),
      aptNo: (v) => setAptNo(v),
      phoneNo: (v) => setPhoneNo(v),
      infoNote: (v, opts) => setInfoNote((prev) => (opts && opts.append ? (prev ? prev + " " : "") + v : v)),
    });
  }, [registerSetters]);

  React.useImperativeHandle(apiRef, () => ({
    submit: () => {
      if (formRef.current) formRef.current.requestSubmit();
    },
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fullName.trim() || !plateNo.trim()) return;
    onSave({
      fullName: fullName.trim(),
      plateNo: plateNo.trim().toUpperCase(),
      carModel: carModel.trim() || "Belirtilmemiş",
      relationDegree: relationDegree.trim() || "Belirtilmemiş",
      aptNo: aptNo.trim() || "Merkez",
      phoneNo: phoneNo.trim() || "Belirtilmemiş",
      infoNote: infoNote.trim() || "",
    });
    setFullName("");
    setPlateNo("");
    setCarModel("");
    setRelationDegree("");
    setAptNo("");
    setPhoneNo("");
    setInfoNote("");
  };

  const shared = { activeField, setActiveField };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={onBack}
          className="text-xs bg-slate-800 text-slate-300 hover:text-white px-3 py-1.5 rounded-lg border border-slate-700 flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={12} /> Geri Dön
        </button>
        <div className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-lg flex items-center gap-1">
          <Info size={11} /> Sıralı kutular sesle dolar
        </div>
      </div>

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="bg-slate-800/90 border border-slate-700 p-5 rounded-2xl shadow-xl space-y-3"
      >
        <Field id="fullName" label="Adı Soyadı" icon={User} value={fullName} onChange={setFullName} placeholder="Adı Soyadı..." {...shared} />
        <Field id="plateNo" label="Plaka No" icon={CreditCard} value={plateNo} onChange={setPlateNo} placeholder="Plaka no..." uppercase {...shared} />
        <Field id="carModel" label="Araç Marka ve Model" icon={Car} value={carModel} onChange={setCarModel} placeholder="Araç modeli..." {...shared} />
        <Field id="relationDegree" label="Yakınlık Derecesi" icon={Tags} value={relationDegree} onChange={setRelationDegree} placeholder="Örn: Ev Sahibi / Kiracı..." {...shared} />

        <div className="grid grid-cols-2 gap-3">
          <Field id="aptNo" label="Daire No" icon={Home} value={aptNo} onChange={setAptNo} placeholder="Daire..." {...shared} />
          <Field id="phoneNo" label="Telefon No" icon={Phone} type="tel" value={phoneNo} onChange={setPhoneNo} placeholder="Telefon..." {...shared} />
        </div>

        <Field id="infoNote" label="Bilgi Notu (İsteğe Bağlı)" icon={StickyNote} value={infoNote} onChange={setInfoNote} placeholder="Kişi veya araç hakkında notlar..." textarea {...shared} />

        <button
          type="submit"
          id="btnSaveRecord"
          className="w-full bg-brand-600 hover:bg-brand-500 text-white font-bold py-3 rounded-xl shadow-lg shadow-brand-600/30 transition-colors mt-2 active:scale-95 flex items-center justify-center gap-2"
        >
          <Save size={15} /> Kaydı Tamamla
        </button>
      </form>
    </div>
  );
}
